import { Check, Rocket, Sparkles } from "lucide-react";
import { AnimatePresence, motion, type Variants } from "motion/react";
import type React from "react";
import { BrowserFrame } from "@/components/blocks/frames/Frames";
import { EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const STAGES = ["generate", "review", "tests", "live"] as const;
const AI_FLOW_DURATIONS = [3200, 3600, 3200, 2800] as const;
/** Line after which the reviewer's fix is inserted. */
const FIX_AFTER = 1;

/* Same variant pattern as the pipeline: children inherit the panel state. */
const panel: Variants = {
	hidden: { opacity: 0, y: 10 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: EASE } },
	exit: { opacity: 0, y: -8, transition: { duration: 0.25, ease: EASE } },
};
const line: Variants = {
	hidden: { opacity: 0, x: -6 },
	visible: (i = 0) => ({
		opacity: 1,
		x: 0,
		transition: { duration: 0.3, ease: EASE, delay: 0.15 + i * 0.32 },
	}),
};
const late: Variants = {
	hidden: { opacity: 0, y: 6 },
	visible: (delay = 0) => ({
		opacity: 1,
		y: 0,
		transition: { duration: 0.4, ease: EASE, delay },
	}),
};
const pop: Variants = {
	hidden: { scale: 0 },
	visible: (i = 0) => ({
		scale: 1,
		transition: { ...SPRING, delay: 0.35 + i * 0.5 },
	}),
};

/**
 * AI-assisted development as a reviewed workflow: the AI drafts a change, a
 * developer reviews and amends it, tests and builds pass, the version ships.
 * Cycles on its own; the server renders the passing tests.
 */
export function AiFlowScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const flow = dict.scenes.aiFlow;
	const { ref, index } = useSceneLoop(AI_FLOW_DURATIONS, { initial: 2 });
	const stage = STAGES[index] ?? "tests";
	const code = Object.values(flow.code);

	const codeLines = (review: boolean) => (
		<div className="relative rounded-lg border bg-muted/40 py-2.5 font-mono text-[10px] leading-[1.75] sm:text-[10.5px]">
			{code.map((text, row) => (
				<div key={text}>
					<motion.p
						variants={review ? undefined : line}
						custom={row}
						className="flex gap-2.5 truncate px-3"
					>
						<span className="text-signal-ink">+</span>
						<span className="truncate whitespace-pre">{text}</span>
					</motion.p>
					{row === FIX_AFTER ? (
						<motion.p
							variants={late}
							custom={review ? 1.9 : 0}
							className={cn(
								"flex gap-2.5 truncate px-3",
								review ? "bg-signal/10" : "invisible",
							)}
						>
							<span className="text-signal-ink">+</span>
							<span className="truncate whitespace-pre">{flow.fix}</span>
						</motion.p>
					) : null}
				</div>
			))}
			{review ? (
				<motion.div
					variants={late}
					custom={0.5}
					className="absolute right-2.5 bottom-2.5 flex max-w-[78%] items-start gap-2 rounded-lg border bg-background p-2 font-sans shadow-sm"
				>
					<span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground font-mono text-[8px] font-semibold text-background">
						{flow.reviewer}
					</span>
					<span className="text-[10px] leading-snug">{flow.comment}</span>
				</motion.div>
			) : null}
		</div>
	);

	const body: Record<(typeof STAGES)[number], React.ReactNode> = {
		generate: (
			<Panel
				title={flow.request}
				aside={
					<span className="flex items-center gap-1 rounded-full bg-signal/10 px-2 py-0.5 text-[10px] font-medium text-signal-ink">
						<Sparkles className="size-3" />
						{flow.aiChip}
					</span>
				}
			>
				{codeLines(false)}
			</Panel>
		),
		review: (
			<Panel
				title={flow.request}
				aside={
					<span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-medium">
						{flow.reviewChip}
					</span>
				}
			>
				{codeLines(true)}
			</Panel>
		),
		tests: (
			<Panel title={flow.checksTitle}>
				<ul className="space-y-2">
					{Object.entries(flow.checks).map(([key, check], row) => (
						<li
							key={key}
							className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2.5 text-[11px]"
						>
							<span className="min-w-0 flex-1 truncate font-medium">
								{check.label}
							</span>
							<span className="font-mono text-[10px] text-muted-foreground">
								{check.value}
							</span>
							<motion.span
								className="flex size-4 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
								variants={pop}
								custom={row}
							>
								<Check className="size-2.5" strokeWidth={3} />
							</motion.span>
						</li>
					))}
				</ul>
			</Panel>
		),
		live: (
			<Panel title={flow.live.title}>
				<motion.div
					variants={late}
					custom={0.15}
					className="flex items-center gap-4 rounded-xl border border-signal/50 bg-signal/10 p-4"
				>
					<motion.span
						variants={pop}
						custom={0}
						className="flex size-10 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
					>
						<Rocket className="size-4.5" />
					</motion.span>
					<span className="min-w-0">
						<span className="block font-mono text-[12px] font-semibold">
							{flow.live.version}
						</span>
						<span className="block text-[11px] text-pretty text-muted-foreground">
							{flow.live.detail}
						</span>
					</span>
				</motion.div>
				<motion.p
					variants={late}
					custom={0.7}
					className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground"
				>
					<Check className="size-3.5 text-signal-ink" strokeWidth={2.5} />
					{flow.live.signoff}
				</motion.p>
			</Panel>
		),
	};

	return (
		<div ref={ref} className={cn("w-full", className)}>
			<BrowserFrame host={flow.file} tag={dict.common.illustrationBadge}>
				<div className="border-b px-4 pt-3">
					<ol className="grid grid-cols-4 gap-2">
						{STAGES.map((key, stageIndex) => (
							<li
								key={key}
								className={cn(
									"truncate pb-2.5 text-[10.5px] font-medium transition-colors duration-300",
									stageIndex === index
										? "text-foreground"
										: stageIndex < index
											? "text-muted-foreground"
											: "text-muted-foreground/70",
								)}
							>
								<span className="mr-1.5 font-mono text-[9px] text-signal-ink">
									0{stageIndex + 1}
								</span>
								{flow.stages[key]}
							</li>
						))}
					</ol>
					<div className="relative -mx-4 h-0.5 bg-foreground/[0.06]">
						<motion.span
							className="absolute inset-y-0 left-0 w-full origin-left bg-signal"
							initial={false}
							animate={{ scaleX: (index + 1) / STAGES.length }}
							transition={SPRING}
						/>
					</div>
				</div>
				<div className="min-h-[13.5rem] p-4">
					<AnimatePresence mode="wait" initial={false}>
						<motion.div
							key={stage}
							variants={panel}
							initial="hidden"
							animate="visible"
							exit="exit"
						>
							{body[stage]}
						</motion.div>
					</AnimatePresence>
				</div>
			</BrowserFrame>
		</div>
	);
}

function Panel({
	title,
	aside,
	children,
}: {
	title: string;
	aside?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<div>
			<div className="mb-3 flex items-center justify-between gap-3">
				<p className="truncate text-xs font-semibold">{title}</p>
				{aside}
			</div>
			{children}
		</div>
	);
}
